/* eslint-disable max-lines-per-function */
import {ContentCardBaseComponent} from 'custom-extensions/ui-components/x-content-card-base';
import {defineCustomElement, formatPrice} from 'custom-extensions/ui-utils';
import {html, nothing} from 'lit';
import GTM from 'extensions/gtm';
import stylesheet from './x-product-card.scss?as=stylesheet';

export class ProductCardComponent extends ContentCardBaseComponent {
	static get styles () {
		return [
			...super.styles,
			stylesheet
		];
	}

	static get properties () {
		return {
			attributeCodes: {
				attribute: 'attribute-codes',
				type: Array
			},
			basePrice: {
				attribute: 'base-price',
				type: Number
			},
			category: {
				type: String
			},
			code: {
				type: String
			},
			hideAttributeImages: {
				attribute: 'hide-attribute-images',
				type: Number
			},
			list: {
				attribute: 'list',
				type: String
			},
			options: {
				type: Array
			},
			position: {
				attribute: 'position',
				type: Number
			},
			price: {
				type: Number
			}
		};
	}

	get formattedBasePrice () {
		return formatPrice(this.basePrice);
	}

	get formattedPrice () {
		if (isNaN(this.price) || this.price === 0) {
			return nothing;
		}

		return formatPrice(this.price);
	}

	constructor () {
		super();

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);

		/**
		 * @type {array}
		 */
		this.attributeCodes = [];

		/**
		 * @type {number}
		 */
		this.basePrice = 0;

		/**
		 * @type {string}
		 */
		this.category = '';

		/**
		 * @type {string}
		 */
		this.code = '';

		/**
		 * @type {number}
		 */
		this.hideAttributeImages = 0;

		/**
		 * @type {string}
		 */
		this.list = '';

		/**
		 * @type {array}
		 */
		this.options = [];

		/**
		 * @type {number}
		 */
		this.position = 0;

		/**
		 * @type {number}
		 */
		this.price = 0;
	}

	updated (changedProperties) {
		if (changedProperties.has('code')) {
			this.gtm.debouncedSendImpressions(this.list, {
				category: this.category,
				id: this.code,
				list: this.list,
				name: this.name,
				position: this.position,
				price: parseFloat(this.price)
			});
		}
	}

	handleClick () {
		this.gtm.sendProductClick({
			category: this.category,
			id: this.code,
			list: this.list,
			name: this.name,
			position: this.position,
			price: this.price
		});
	}

	renderImage () {
		return html`
			<div class="image">
				<x-image src="${this.imageUrl}" alt="${this.name} image"></x-image>
				${this.renderOptionSwatches()}
			</div>
		`;
	}

	get formattedOptions () {
		let formattedOptions = [];
		if (this.options?.length) {
			for (const option of this.options) {
				formattedOptions.push(option.code);
			}
		} else {
			formattedOptions = this.attributeCodes;
		}

		return formattedOptions;
	}

	renderOptionSwatches () {
		if (this.hideAttributeImages || this.formattedOptions?.length <= 1) {
			return nothing;
		}

		const swatches = [];
		for (const option of this.formattedOptions) {
			if ((/\d/).test(option)) {
				continue;
			}

			let formattedPrompt = option.split('-').join(' ');
			formattedPrompt = formattedPrompt.replace(
				/\w\S*/g,
				text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
			);

			swatches.push(html`<span class="g-product-option__color g-product-option__color-${option}" style="background: var(--option-color-${option}, #CCC);"aria-hidden="true" title="${formattedPrompt}"></span>`);
		}

		if (swatches.length === 0) {
			return nothing;
		}

		return html`
			<div class="product-swatches">${swatches}</div>
		`;
	}

	renderInfoLeft () {
		return this.code;
	}

	renderInfoRight () {
		return this.formattedPrice;
	}
};

defineCustomElement('x-product-card', ProductCardComponent);
